<template>
  <div v-sight-photos-view class="photos-preview">
    <PhotosGrid
      v-if="!isMobile"
      :photos="displayedPhotos"
      :layout="layout"
      @photo-click="onPhotoClick"
      @hero-load="onHeroLoad"
    />
    <MobileScroll v-else :photos="photos" @photo-click="onPhotoClick" @hero-load="onHeroLoad" />

    <PhotosGallery
      v-if="isGalleryRequested && experience?.id"
      ref="galleryRef"
      :photos="photos"
      :experience="experience"
      @ready="onGalleryReady"
    />

    <GalleryButton v-if="morePhotoCount" :count="morePhotoCount" @click="onGalleryButtonClick" />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, ref } from 'vue';

import { useAnalytics } from '@/shared/lib/analytics';
import { useScreenSize } from '@/shared/utils/breakpoints';

import GalleryButton from './GalleryButton.vue';
import MobileScroll from './MobileScroll.vue';
import PhotosGrid from './PhotosGrid.vue';
import { getDisplayedPhotos, getLayoutSlice } from '../lib/get-displayed-photos';
import { getPreviewLayout } from '../lib/get-preview-layout';

import type { ExperiencePhoto, PhotosPreviewExperience } from '../types/experience';

type Props = {
  photos: ExperiencePhoto[];
  experience?: PhotosPreviewExperience;
  experienceId: number;
};

type PhotosGalleryExpose = {
  open: (index: number) => Promise<void>;
};

// пакет галереи (слайдер, модалка) нужен только после клика, поэтому уходит в отдельный чанк
const PhotosGallery = defineAsyncComponent(() => import('./PhotosGallery.vue'));

const props = defineProps<Props>();
// главное фото загрузилось (или не загрузилось): после этого можно грузить всё остальное
const emit = defineEmits<{
  heroLoad: [];
}>();
const { isMobile } = useScreenSize();
const { trackEvent, useViewTracker } = useAnalytics();

const galleryRef = ref<PhotosGalleryExpose | null>(null);
// галерею монтируем после главного фото или по первому клику: так её чанк не мешает LCP
const isGalleryRequested = ref(false);
// клик пришёл раньше, чем галерея загрузилась: открываем её, как только она будет готова
let pendingIndex: number | null = null;

const layout = computed(() => {
  if (!props.photos.length) {
    return 'horizontal-horizontal';
  }
  return getPreviewLayout(props.photos);
});

const displayedPhotos = computed(() => {
  const countOfSlice = getLayoutSlice(layout.value);
  if (!props.photos.length) {
    return Array.from({ length: countOfSlice }, () => ({}) as ExperiencePhoto);
  }
  return getDisplayedPhotos(props.photos, layout.value).slice(0, countOfSlice);
});

const morePhotoCount = computed(() => props.photos.length);

const vSightPhotosView = useViewTracker('sight:photos.view');

const getPhotoId = (index: number) => props.photos[index]?.id ?? index;

const handleTriggerClick = (index: number) => trackEvent('sight:photos.click', { photo_id: getPhotoId(index) });

const onHeroLoad = () => {
  isGalleryRequested.value = true;
  emit('heroLoad');
};

const openGallery = async (index: number) => {
  if (galleryRef.value) {
    await galleryRef.value.open(index);
    return;
  }

  pendingIndex = index;
  isGalleryRequested.value = true;
};

const onGalleryReady = async () => {
  if (pendingIndex === null) {
    return;
  }

  const index = pendingIndex;
  pendingIndex = null;
  // ref на асинхронный компонент проставляется после монтирования
  await nextTick();
  await galleryRef.value?.open(index);
};

const onPhotoClick = async (index: number) => {
  void handleTriggerClick(index);
  await openGallery(index);
};

const onGalleryButtonClick = () => {
  void handleTriggerClick(0);
  void openGallery(0);
};
</script>

<style scoped lang="scss">
.photos-preview {
  position: relative;
  isolation: isolate;
}
</style>
